import { useState } from "react";
import { format, parseISO } from "date-fns";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Pencil, Trash2, Container, Archive, ArchiveRestore, MapPin, Building2, User } from "lucide-react";
import { VesselWithRelations, useUpdateVessel, useDeleteVessel, useVesselDependencies, useArchiveVessel, useUnarchiveVessel } from "@/hooks/useVessels";
import { useFacilities } from "@/hooks/useFacilities";
import { useClients } from "@/hooks/useClients";
import { useVolumeUnit } from "@/hooks/useOrganization";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  VESSEL_KINDS,
  TANK_SUBTYPES,
  formatVesselKind,
  formatVesselSubtypes,
} from "@/lib/vessel-types";

interface VesselDetailSheetProps {
  vessel: VesselWithRelations | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function VesselDetailSheet({ vessel, open, onOpenChange }: VesselDetailSheetProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState("");
  const [vesselKind, setVesselKind] = useState("");
  const [vesselSubtypes, setVesselSubtypes] = useState<string[]>([]);
  const [capacity, setCapacity] = useState("");
  const [capacityTons, setCapacityTons] = useState("");
  const [preferredCapacityUnit, setPreferredCapacityUnit] = useState<'gallons' | 'tons' | ''>("");
  const [facilityId, setFacilityId] = useState("");
  const [ownerClientId, setOwnerClientId] = useState("");
  const [manufacturer, setManufacturer] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);

  const updateVessel = useUpdateVessel();
  const deleteVessel = useDeleteVessel();
  const archiveVessel = useArchiveVessel();
  const unarchiveVessel = useUnarchiveVessel();
  const { data: dependencies, isLoading: dependenciesLoading } = useVesselDependencies(vessel?.id || null);
  const { data: facilities = [] } = useFacilities();
  const { data: clients = [] } = useClients();
  const { volumeAbbrev } = useVolumeUnit();

  const isArchived = vessel?.is_active === false;

  const handleEdit = () => {
    if (vessel) {
      setName(vessel.name);
      setVesselKind(vessel.vessel_kind || "");
      setVesselSubtypes(vessel.vessel_subtypes || []);
      setCapacity(vessel.capacity_l?.toString() || "");
      setCapacityTons(vessel.capacity_tons?.toString() || "");
      setPreferredCapacityUnit((vessel.preferred_capacity_unit as 'gallons' | 'tons' | null) ?? "");
      setFacilityId(vessel.facility_id || "");
      setOwnerClientId(vessel.owner_client_id || "");
      setManufacturer(vessel.manufacturer || "");
      setIsEditing(true);
    }
  };

  const handleSave = () => {
    if (vessel) {
      updateVessel.mutate(
        {
          id: vessel.id,
          input: {
            name,
            vessel_kind: vesselKind || undefined,
            vessel_subtypes: vesselKind === 'tank' ? vesselSubtypes : [],
            capacity_l: capacity ? parseFloat(capacity) : undefined,
            capacity_tons: capacityTons ? parseFloat(capacityTons) : null,
            preferred_capacity_unit: preferredCapacityUnit || null,
            facility_id: facilityId || null,
            owner_client_id: ownerClientId || null,
            manufacturer: manufacturer || null,
          },
        },
        {
          onSuccess: () => setIsEditing(false),
        }
      );
    }
  };

  const handleDelete = () => {
    if (vessel) {
      deleteVessel.mutate(vessel.id, {
        onSuccess: () => {
          setShowDeleteDialog(false);
          onOpenChange(false);
        },
      });
    }
  };

  const handleArchive = () => {
    if (vessel) {
      archiveVessel.mutate(vessel.id, {
        onSuccess: () => {
          setShowArchiveDialog(false);
          onOpenChange(false);
        },
      });
    }
  };

  const handleUnarchive = () => {
    if (vessel) {
      unarchiveVessel.mutate(vessel.id, {
        onSuccess: () => {
          onOpenChange(false);
        },
      });
    }
  };

  if (!vessel) return null;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Vessel Details</SheetTitle>
          </SheetHeader>

          <div className="space-y-6 py-6">
            {isEditing ? (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit-name">Vessel Name</Label>
                  <Input
                    id="edit-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-facility">Facility</Label>
                  <Select value={facilityId || "__none__"} onValueChange={(v) => setFacilityId(v === "__none__" ? "" : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select facility" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">No facility</SelectItem>
                      {facilities.map((f) => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Type</Label>
                  <div className="flex rounded-md border overflow-hidden mt-1.5 text-sm w-fit">
                    {VESSEL_KINDS.map((kind, i) => (
                      <button
                        key={kind.value}
                        type="button"
                        onClick={() => {
                          setVesselKind(kind.value);
                          setVesselSubtypes([]);
                        }}
                        className={[
                          "px-4 py-1.5 transition-colors",
                          i > 0 ? "border-l" : "",
                          vesselKind === kind.value
                            ? "bg-muted font-medium text-foreground"
                            : "text-muted-foreground hover:text-foreground",
                        ].join(" ")}
                      >
                        {kind.label}
                      </button>
                    ))}
                  </div>
                </div>
                {vesselKind === 'tank' && (
                  <div>
                    <Label>Tank Use</Label>
                    <p className="text-xs text-muted-foreground mb-2">Select all that apply</p>
                    <div className="space-y-2">
                      {TANK_SUBTYPES.map(subtype => (
                        <div key={subtype.value} className="flex items-center gap-2">
                          <Checkbox
                            id={`edit-subtype-${subtype.value}`}
                            checked={vesselSubtypes.includes(subtype.value)}
                            onCheckedChange={(checked) =>
                              setVesselSubtypes(prev =>
                                checked
                                  ? [...prev, subtype.value]
                                  : prev.filter(s => s !== subtype.value)
                              )
                            }
                          />
                          <Label htmlFor={`edit-subtype-${subtype.value}`} className="font-normal cursor-pointer">
                            {subtype.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-capacity">Capacity (Gal)</Label>
                    <Input
                      id="edit-capacity"
                      type="number"
                      value={capacity}
                      onChange={(e) => setCapacity(e.target.value)}
                      step="0.01"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-capacity-tons">Capacity (Tons)</Label>
                    <Input
                      id="edit-capacity-tons"
                      type="number"
                      value={capacityTons}
                      onChange={(e) => setCapacityTons(e.target.value)}
                      step="0.1"
                    />
                  </div>
                </div>
                {capacity && capacityTons && (
                  <div>
                    <Label>Display capacity as</Label>
                    <div className="flex rounded-md border overflow-hidden text-sm mt-1.5 w-fit">
                      {(['gallons', '', 'tons'] as const).map((opt, i) => (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => setPreferredCapacityUnit(opt)}
                          className={[
                            "px-3 py-1.5 transition-colors",
                            i > 0 ? "border-l" : "",
                            preferredCapacityUnit === opt
                              ? "bg-muted font-medium text-foreground"
                              : "text-muted-foreground hover:text-foreground",
                          ].join(" ")}
                        >
                          {opt === 'gallons' ? 'Gallons' : opt === 'tons' ? 'Tons' : 'Auto'}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Auto shows gallons by default. Override during harvest to show tonnage.
                    </p>
                  </div>
                )}
                <div>
                  <Label htmlFor="edit-manufacturer">Manufacturer</Label>
                  <Input
                    id="edit-manufacturer"
                    value={manufacturer}
                    onChange={(e) => setManufacturer(e.target.value)}
                    placeholder="e.g., Westec, Custom Metalcraft"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-owner">Owner (leave empty for winery-owned)</Label>
                  <Select value={ownerClientId || "__none__"} onValueChange={(v) => setOwnerClientId(v === "__none__" ? "" : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Winery-owned" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Winery-owned</SelectItem>
                      {clients.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleSave} disabled={updateVessel.isPending}>
                    Save Changes
                  </Button>
                  <Button variant="outline" onClick={() => setIsEditing(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                      <Container className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-2xl font-bold">{vessel.name}</h3>
                        {isArchived && (
                          <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                            archived
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <Badge variant="secondary">{formatVesselKind(vessel.vessel_kind)}</Badge>
                        {vessel.vessel_subtypes?.length ? (
                          vessel.vessel_subtypes.map(s => (
                            <Badge key={s} variant="outline" className="text-xs">
                              {formatVesselSubtypes([s])}
                            </Badge>
                          ))
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Details Section */}
                <div className="space-y-3">
                  <h4 className="font-semibold">Details</h4>
                  <div className="space-y-2">
                    {vessel.facility && (
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Facility:</span>
                        <span className="font-medium">{vessel.facility.name}</span>
                      </div>
                    )}
                    {vessel.manufacturer && (
                      <div className="flex items-center gap-2 text-sm">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Manufacturer:</span>
                        <span className="font-medium">{vessel.manufacturer}</span>
                      </div>
                    )}
                    {vessel.owner_client && (
                      <div className="flex items-center gap-2 text-sm">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Owner:</span>
                        <span className="font-medium">{vessel.owner_client.name}</span>
                      </div>
                    )}
                  </div>
                </div>

                <Separator />

                <div className="space-y-4">
                  <h4 className="font-semibold">Capacity</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-muted/50">
                      <p className="text-2xl font-bold">
                        {vessel.capacity_l ? Math.round(vessel.capacity_l).toLocaleString() : "—"}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">Gallons</p>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/50">
                      <p className="text-2xl font-bold">
                        {vessel.capacity_tons ? vessel.capacity_tons : "—"}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">Tons</p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="flex gap-2">
                  {isArchived ? (
                    <>
                      <Button
                        variant="outline"
                        onClick={handleUnarchive}
                        disabled={unarchiveVessel.isPending}
                        className="flex-1"
                      >
                        <ArchiveRestore className="mr-2 h-4 w-4" />
                        {unarchiveVessel.isPending ? "Restoring..." : "Restore"}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setShowDeleteDialog(true)}
                        disabled={dependenciesLoading || !dependencies?.canDelete}
                        className="flex-1 text-destructive hover:text-destructive disabled:text-muted-foreground disabled:hover:text-muted-foreground"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button variant="outline" onClick={handleEdit} className="flex-1">
                        <Pencil className="mr-2 h-4 w-4" />
                        Edit
                      </Button>
                      {dependencies?.canDelete ? (
                        <Button
                          variant="outline"
                          onClick={() => setShowDeleteDialog(true)}
                          className="flex-1 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          onClick={() => setShowArchiveDialog(true)}
                          disabled={archiveVessel.isPending}
                          className="flex-1"
                        >
                          <Archive className="mr-2 h-4 w-4" />
                          Archive
                        </Button>
                      )}
                    </>
                  )}
                </div>

                {/* References - shown as informational note at bottom */}
                {dependencies && dependencies.assignments.length > 0 && (
                  <div className="space-y-3 pt-2">
                    <Separator />
                    <p className="text-xs text-muted-foreground">
                      Referenced by:
                    </p>

                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground font-medium">
                        Lot Assignments ({dependencies.assignmentCount})
                      </p>
                      {dependencies.assignments.map((assignment) => (
                        <div
                          key={assignment.id}
                          className="flex items-center justify-between text-sm text-muted-foreground"
                        >
                          <span>{assignment.wine_lot_code || 'Unknown Lot'}</span>
                          <span className="text-xs">
                            {format(parseISO(assignment.fill_date), 'MMM d, yyyy')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Vessel</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {vessel.name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Vessel</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to archive {vessel.name}? The vessel will be hidden from active lists but can be restored later. Historical data will be preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchive}>
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
